/* eslint-disable quotes */
import { BlockBlobClient, BlockBlobParallelUploadOptions } from '@azure/storage-blob'
import {
	AttachmentDeleteResponse,
	AttachmentInfo,
	AttachmentsUploadResponse,
	AttendanceAttachment,
	BulkUploadSmallAttachment,
} from '../types/Upload/AttachmentResponse'
import {
	deleteOneWithResponse,
	getAll,
	getOne,
	postWithResponse,
} from './CommonServices/httpService'
import { showErrorToast } from './CommonServices/alertServices'
import { AttachmentWithTempId } from '../types/Upload/Attachment'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import {
	getAttachmentFB,
	listAttachmentsFB,
	uploadAttachmentsFB,
} from './CacheFallbackServices/attachmentFallback'
import { hasConnection } from './CommonServices/healthGate'
import { processWithConcurrency } from './CommonServices/httpService'

// TODO: Split into two functions, one for upload one for annotation save
const uploadAttachments = async (
	files: File[],
	itemId: number,
	overwrite: boolean,
	type: 'workitem' | 'event',
	newAnnotations?: string,
	setUploadProgress:
		| React.Dispatch<
				React.SetStateAction<
					| {
							fileName: string
							progress: number
					  }[]
					| undefined
				>
		  >
		| undefined = undefined,
	db: ForemanDB | null = null,
): Promise<boolean> => {
	try {
		if (
			!(await hasConnection()) &&
			db &&
			type === 'workitem' &&
			newAnnotations &&
			overwrite &&
			files.length === 1
		) {
			try {
				await uploadAttachmentsFB(db, files[0], itemId, newAnnotations)
				return true
			} catch (error: any) {
				showErrorToast({ message: error.message || 'Failed to upload attachment in offline mode' })
				return false
			}
		}

		const filesWithTempId = files.map(getTempIdObject);
		const requestsData = filesWithTempId.map((obj) => ({
			FileName: decodeURIComponent(obj.file.name),
			Name: decodeURIComponent(obj.file.name.split('.')[0]),
			TempId: obj.tempId,
		}))

		const url =
			type === 'workitem'
				? `/WorkItems/${itemId}?overwrite=${overwrite}`
				: `/ChangeOrders/Event/${itemId}/Attachment?overwrite=${overwrite}`
		const tokenRequests = await postWithResponse<AttachmentsUploadResponse[]>(url, requestsData)
		if (tokenRequests.data.length !== files.length)
			throw new Error('Records inserted but upload files failed.')
		// Match the response data (IDs) with the files
		const uploadTasks = filesWithTempId.map((fileWithTempId) => {
			const token = tokenRequests.data.find((a) => a.tempId === fileWithTempId.tempId)

			// Create a BlobClient for the current file
			const blockBlobClient = new BlockBlobClient(`${token?.uploadUri}${token?.sasToken}`)

			const options: BlockBlobParallelUploadOptions = {
				blobHTTPHeaders: { blobContentType: fileWithTempId.file.type },
				onProgress: (progressEvent) => {
					if (progressEvent.loadedBytes && fileWithTempId.file.size) {
						const progress = Math.round((progressEvent.loadedBytes / fileWithTempId.file.size) * 100)
						setUploadProgress &&
							setUploadProgress((prevItems) =>
								prevItems?.map((item) =>
									item.fileName === decodeURIComponent(fileWithTempId.file.name) ? { ...item, progress } : item,
								),
							)
					}
				},
			}

			// Upload file
			return blockBlobClient.uploadData(fileWithTempId.file, options)
		})
		await Promise.all(uploadTasks)
		return true
	} catch (err: any) {
		showErrorToast({ message: err.message })
		return false
	}
}

const getTempIdObject = (file: File): { tempId: string; file: File } => {
	// Generates a random string of 16 characters for tempId
	const tempId = Math.random().toString(36).substr(2, 16) + Date.now().toString(36)
	return { tempId, file }
}


const deleteAttachment = async (
	attachmentId: number,
	type: 'workitem' | 'event',
): Promise<boolean> => {
	try {
		const token = await deleteOneWithResponse<AttachmentDeleteResponse>(
			`/Attachments/${type}/${attachmentId}`,
		)
		const blockBlobClient = new BlockBlobClient(`${token?.data.uri}${token?.data.sasToken}`)
		await blockBlobClient.deleteIfExists()

		return true
	} catch (err: any) {
		showErrorToast({ message: err.message })
		return false
	}
}

const listAttachments = async (
	itemId: number,
	type: 'workitem' | 'event',
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await listAttachmentsFB(db, itemId)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to list attachments from cache' })
			return [] as AttachmentInfo[]
		}
	}
	try {
		const result = await getAll<AttachmentInfo[]>(`/attachments/${type}/${itemId}/list`)
		return result.data
	} catch (error: any) {
		return [] as AttachmentInfo[]
	}
}

const getAttachment = async (
	attachmentId: number,
	type: 'workitem' | 'event',
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db && type === 'workitem') {
		try {
			return await getAttachmentFB(db, attachmentId)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get attachment from cache' })
			throw error
		}
	}
	return await getOne(`/attachments/${type}/${attachmentId}`)
}

const bulkInsertAttachments = async (
	attachments: {
		tempId: string
		path: string
	}[],
) => {
	try {
		const result = await postWithResponse<BulkUploadSmallAttachment[]>(
			`/Attachments/Bulk`,
			attachments,
		)
		return result.data
	} catch (error) {
		throw error
	}
}
const bulkUploadAttachmentsToStorage = async (
	attachmentsToUpload: AttachmentWithTempId[],
	setSelectedAttachments: React.Dispatch<React.SetStateAction<AttachmentWithTempId[]>>,
) => {
	const uploadTasks = attachmentsToUpload.map((attach) => {
		const blockBlobClient = new BlockBlobClient(`${attach.uploadUri}${attach.sasToken}`)

		const options: BlockBlobParallelUploadOptions = {
			blobHTTPHeaders: { blobContentType: attach.file.type },
			onProgress: (progressEvent) => {
				if (progressEvent.loadedBytes && attach.file.size) {
					const progress = Math.round((progressEvent.loadedBytes / attach.file.size) * 100)
					setSelectedAttachments &&
						setSelectedAttachments((prevItems) =>
							prevItems?.map((item) =>
								item.tempId === attach.tempId ? { ...item, uploadProgress: progress } : item,
							),
						)
				}
			},
		}

		// Upload file
		return () => blockBlobClient.uploadData(attach.file, options)
	})
	await processWithConcurrency(uploadTasks)
}


const uploadProjectMap = async (file: File, setMapUploadProgress: (progress: number) => void) => {
	const tokenRequests = await postWithResponse<AttachmentsUploadResponse>(
		`/Projects/Map?fileName=${file.name}`,
		{},
	)
	const blockBlobClient = new BlockBlobClient(
		`${tokenRequests?.data.uploadUri}${tokenRequests?.data.sasToken}`,
	)

	const options: BlockBlobParallelUploadOptions = {
		blobHTTPHeaders: { blobContentType: file.type },
		onProgress: (progress) => {
			setMapUploadProgress(Math.round((progress.loadedBytes / file.size) * 100))
		},
	}

	await blockBlobClient.uploadData(file, options)
}

const getProjectMapImage = async () => {
	const tokenRequests = await getOne<AttachmentsUploadResponse>(`/Projects/Map`)
	if (!tokenRequests.data.uploadUri || !tokenRequests.data.sasToken) return
	const blockBlobClient = new BlockBlobClient(
		`${tokenRequests?.data.uploadUri}${tokenRequests?.data.sasToken}`,
	)

	const downloadResponse = await blockBlobClient.download()
	const blobData = await downloadResponse.blobBody
	if (!blobData) return
	const imageUrl = URL.createObjectURL(blobData)
	return imageUrl
}

const deleteProjectMap = async () => {
	const tokenRequests = await deleteOneWithResponse<AttachmentsUploadResponse>(`/Projects/Map`)
	if (!tokenRequests.data) return
	const blockBlobClient = new BlockBlobClient(
		`${tokenRequests?.data.uploadUri}${tokenRequests?.data.sasToken}`,
	)

	const deleteResponse = await blockBlobClient.deleteIfExists()

	return deleteResponse.succeeded
}

const getConsentForms = async (fromDate: string | null, toDate: string | null) => {
	const queryParams = []
	if (fromDate) queryParams.push(`fromDate=${fromDate}`)
	if (toDate) queryParams.push(`toDate=${toDate}`)

	const tokenRequests = await getAll<AttendanceAttachment[]>(
		`/Attachments/Consents${queryParams.length ? '?' + queryParams.join('&') : ''}`,
	)
	return tokenRequests.data
}

export {
	uploadAttachments,
	deleteAttachment,
	listAttachments,
	getAttachment,
	bulkInsertAttachments,
	bulkUploadAttachmentsToStorage,
	uploadProjectMap,
	getProjectMapImage,
	deleteProjectMap,
	getConsentForms,
}
